<template>
  <div>
    <h1>{{ stepState.name }}</h1>
    <div v-if="submitError" class="alert alert-danger" data-submit-error>
      <button type="button" class="close" @click="submitError = false" />
      {{ $t('survey.refreshPage') }}
    </div>

    <div v-if="partialDataLoaded" class="alert alert-info">
      <button type="button" class="close" @click="partialDataLoaded = false">
        <span>&times;</span>
      </button>
      {{ $t('survey.resumeSavedProgress') }}
    </div>

    <div v-if="isFirstPage && showInstructions && hasInstructions" v-html="$sanitize(stepState.instructions)" />
    <div v-if="showConfirmation && hasConfirmation" v-html="$sanitize(confirmationHtml)" />

    <survey
      v-show="showSurvey"
      :key="`survey-${stepState.id}`"
      :survey="stepSurvey"
    />

    <b-spinner v-if="isSubmitting" />

    <button
      v-if="showContinue"
      id="continue"
      type="button"
      class="btn btn-primary"
      @click="$emit('refresh')"
    >
      {{ $t('continue') }}
    </button>
  </div>
</template>

<script>
  import * as SurveyJS from 'survey-vue';
  import '@/FormWidgets/surveyjs/surveyVueLocalization';
  import * as widgets from 'surveyjs-widgets';
  import * as nouislider from 'nouislider';
  import { debounce } from "lodash-es";
  import BaseStep from './BaseStep.vue';
  import {BSpinner} from "bootstrap-vue";

  SurveyJS.Survey.cssType = "bootstrap";
  SurveyJS.defaultBootstrapCss.navigationButton = 'btn btn-default';
  widgets.nouislider(SurveyJS);

  export default {
      components: {
          BSpinner,
          survey: SurveyJS.Survey
      },
      extends: BaseStep,
      data() {
          return {
              showInstructions: true,
              showConfirmation: false,
              showContinue: false,
              showSurvey: true,
              isSubmitting: false,
              stepSurvey: null,
              submitError: false,
              partialDataLoaded: false,
          };
      },
      computed: {
          hasConfirmation() {
              return this.stepState.confirmation != undefined || (this.stepSurvey.showCompletedPage && this.stepSurvey.completedHtml.length > 0);
          },
          hasInstructions() {
              return this.stepState.instructions != undefined;
          },
          isFirstPage() {
              return this.stepSurvey ? this.stepSurvey.isFirstPage : false;
          },
          confirmationHtml() {
            return this.stepState.confirmation || this.stepSurvey.completedHtml;
          }
      },
      methods: {
          init() {
              this.$emit('ready');
              var outerScope = this;
              if (this.stepState.surveySchema) {
                  this.stepSurvey = new SurveyJS.Model(this.stepState.surveySchema);
                  this.stepSurvey.locale = this.$i18n.locale;
                  if (this.stepState.surveyVariables) {
                    Object.keys(this.stepState.surveyVariables).forEach((key) => {
                      this.stepSurvey.setVariable(key, this.stepState.surveyVariables[key]);
                    });
                  }

                if (this.stepState.partialData) {
                  if (this.stepState.partialData.data) {
                    this.stepSurvey.data = {...this.stepState.partialData.data}; // make copy

                    // SurveyJS caches anything you set in data, which is not ideal
                    // for calculated values.  Unset those so they get recalculated.
                    this.stepSurvey.calculatedValues.forEach((val) => {
                      this.stepSurvey.deleteDataValueCore(this.stepSurvey.valuesHash, val.propertyHash.name);
                    });
                  }
                  if (this.stepState.partialData.page) {
                    this.stepSurvey.currentPageNo = this.stepState.partialData.page;
                  }
                  this.partialDataLoaded = true;
                } else {
                  // We want to force the first page when this is a brand new survey, we don't want to skip intro pages without questions
                  this.stepSurvey.currentPageNo = 0;
                }

                // scroll to top when changing pages
                this.stepSurvey
                  .onCurrentPageChanged
                  .add(function () {
                    setTimeout(function(){
                      window.scroll(0,0);
                    }, 10);
                  });

                // don't set up the watchers until after we've initialized (and possibly loaded) the surveyJsModel
                this.$watch('stepSurvey.data', debounce(this.updatePartialData, 500), { deep: true });
                this.$watch('stepSurvey.currentPageNo', debounce(this.updatePartialData, 500), { deep: true });

                this.stepSurvey.onComplete.add(this.submitForm);
              }
          },
          async updatePartialData() {
            try {
              await this.$store.dispatch('SAVE_PARTIAL_SURVEY', {
                api_id: this.stepState.actions.savePartialData.params.api_id,
                data: {
                  page: this.stepSurvey.currentPageNo,
                  data: this.stepSurvey.data
                }
              });
            } catch (err) {
              // what do we do if we get an error here?
            }
          },
          submitForm(sender, options) {
              this.isSubmitting = true;
              options.showSaveInProgress();
              this.showContinue = false;
              this.showSurvey = false;
              this.stepSurvey.showNavigationButtons = false;

              this.$api.DataSources.createSubmissionForDataSource({
                  'source_id': this.stepState.actions.submit.params.source_id,
                  'submission': {
                      'participant_id': this.participantId,
                      'fields': this.stepSurvey.data
                  }
              }).then(({body, status}) => {
                  this.isSubmitting = false;

                  if (status >= 400) {
                      options.showSaveError();
                      throw {status: status, body: body};
                  }
                  options.showSaveSuccess();
                  this.submitError = false;
                  this.showInstructions = false;
                  this.showConfirmation = true;
                  // if no confirmation in enrollment step nor surveyjs, refresh on complete button
                  if (!this.hasConfirmation) {
                      this.$emit('refresh');
                      return;
                  }
                  this.showContinue = true;
              }).catch((error) => {
                  this.isSubmitting = false;
                  this.submitError = true;
                  options.showSaveError();
                  this.stepSurvey.showNavigationButtons = true;
              });
          }
      }
  }

</script>

<style>
label > small {
  font-weight: normal;
  font-style: italic;
}

.radio > label > input {
  opacity: 1 !important;
  z-index: auto;
}

.panel-footer.card-footer {
  background: none;
  border-top: none;
}
</style>
